import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { map, tap } from 'rxjs/operators';
import { EnterpriseTypeEnum } from '../enums/enterprise-type-enum';
import { EmployerDto, EnterpriseCustomerV2Dto, EnterpriseRestService } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseCustomerService {
  private employerDto: EmployerDto = null;
  private isAdmin = false;

  private enterpriseCustomer: EnterpriseCustomerV2Dto[] = [];
  private enterpriseCustomer$ = new BehaviorSubject<EnterpriseCustomerV2Dto[]>([]);
  private enterpriseCustomerWD: EnterpriseCustomerV2Dto[] = [];
  private enterpriseCustomerWD$ = new BehaviorSubject<EnterpriseCustomerV2Dto[]>([]);

  // Default EXA colors
  defaultColor = '#27a89a';
  defaultName = '';

  constructor(
    private auth: AuthService,
    private employerService: EmployerService,
    private enterpriseRest: EnterpriseRestService,
    private errorHandler: ErrorHandlerService,
    private alertService: AlertService,
    private translate: TranslateService,
    private log: NGXLogger
  ) {
   
  }

  loadEnterpriseCustomers() {
     this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;
      if (this.employerDto) {
        this.isAdmin = this.auth.hasGrant(ExaGrant.ADMIN) || this.auth.hasGrantOwner(ExaGrant.ENTERPRISE_ADMIN, this.employerDto.id);
      }
      this.reloadEnterpriseCustomers();
    });
    this.log.debug('Construct');
  }

  reloadEnterpriseCustomers() {
    this.enterpriseCustomer = [];
    this.enterpriseCustomerWD = [];
    if (this.employerDto && this.employerDto.type == EnterpriseTypeEnum.ENTERPRISE) {
      this.enterpriseRest.getCustomersUsingPOST(this.employerDto.username).subscribe(
        (enterpriseCustomer) => {
          this.enterpriseCustomerWD = enterpriseCustomer.content;
          this.enterpriseCustomer = this.filterDeletedElements(this.enterpriseCustomerWD);
          this.enterpriseCustomer$.next(this.enterpriseCustomer);
          this.enterpriseCustomerWD$.next(this.enterpriseCustomerWD);
        },
        (err) => {
          this.enterpriseCustomer = [];
          this.enterpriseCustomerWD = [];
          this.enterpriseCustomer$.next(this.enterpriseCustomer);
          this.enterpriseCustomerWD$.next(this.enterpriseCustomerWD);
          this.errorHandler.handleResponseError(err);
        }
      );
    } else {
      this.enterpriseCustomer$.next(this.enterpriseCustomer);
      this.enterpriseCustomerWD$.next(this.enterpriseCustomerWD);
    }
  }

  private filterDeletedElements(elementsWithDeleted: EnterpriseCustomerV2Dto[]): EnterpriseCustomerV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  private filterNonDeletedElements(elementsWithDeleted: EnterpriseCustomerV2Dto[]): EnterpriseCustomerV2Dto[] {
    return elementsWithDeleted.filter((elem) => elem.deleted);
  }

  onGetEnterpriseCustomers(withDeleted: boolean): Observable<EnterpriseCustomerV2Dto[]> {
    if (withDeleted) {
      return this.enterpriseCustomerWD$;
    }
    return this.enterpriseCustomer$;
  }

  getCustomersInactive(): EnterpriseCustomerV2Dto[] {
    return this.filterNonDeletedElements(this.enterpriseCustomerWD);
  }

  onGetEnterpriseCustomersSorted(withDeleted: boolean): Observable<EnterpriseCustomerV2Dto[]> {
    this.loadEnterpriseCustomers();
    return this.onGetEnterpriseCustomers(withDeleted).pipe(
      map((arr) =>
        arr.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        })
      )
    );
  }

  getCustomerById(customerId: number) {
    if (customerId) {
      for (let customer of this.enterpriseCustomerWD) {
        if (customer.id == customerId) {
          return customer;
        }
      }
    }
    return null;
  }

  getCustomerColor(customerId: number) {
    if (customerId) {
      for (let customer of this.enterpriseCustomerWD) {
        if (customer.id == customerId) {
          if (customer.color) {
            return '#' + customer.color;
          } else {
            return this.defaultColor;
          }
        }
      }
    }
    return this.defaultColor;
  }

  getCustomerName(customerId: number) {
    if (customerId) {
      for (let customer of this.enterpriseCustomerWD) {
        if (customer.id == customerId) {
          if (customer.name) {
            return customer.name;
          } else {
            return this.defaultName;
          }
        }
      }
    }
    return this.defaultName;
  }

  addCustomer(enterpriseUsername: string, customerDto: EnterpriseCustomerV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.enterpriseRest.addCustomerUsingPOST(enterpriseUsername, customerDto).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('CUSTOMER.ADD.SUCCESS'));
          console.log(' customer hinzugefügt.');
          this.reloadEnterpriseCustomers();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  editCustomer(customerId: number, customerDto: EnterpriseCustomerV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.enterpriseRest.updateCustomerUsingPUT(customerId, customerDto).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('CUSTOMER.EDIT.SUCCESS'));
          console.log(' customer editiert.');
          this.reloadEnterpriseCustomers();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteCustomer(customerId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.enterpriseRest.deleteCustomerUsingDELETE(customerId).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('CUSTOMER.DELETE.SUCCESS'));
          console.log(' customer gelöscht.');
          this.reloadEnterpriseCustomers();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }
}
